'use client';

import { OddsConsentModal } from '@hyphengroup/cms-components/components/OddsConsentModal/OddsConsentModal';
import { AgreementsData } from '@hyphengroup/cms-components/components/OddsConsentModal/types';
import { SlideUpModalHandle } from '@hyphengroup/cms-components/components/SlideUpModal';
import { useCallback, useMemo, useRef } from 'react';
import { languageCode } from 'utils/locale';
import { isApprovalOddsTooStale } from './isApprovalOddsTooStale';
import type { UseApprovalOddsReturn } from './useApprovalOdds';
import { pushDataLayer } from '$/components/cms/pushDataLayer';
import { useIdentity } from '$/modules/auth/hooks';

type UseApprovalOddsRetrievalParams = {
  approvalOddsData: UseApprovalOddsReturn['data'];
  sendConsent: UseApprovalOddsReturn['sendConsent'];
  langPrefix: string;
  chcBaseUrl: string | undefined;
};

export type UseApprovalOddsRetrievalReturn = {
  /**
   * Handles both unlocking (first retrieval) and refreshing of approval odds:
   * login -> consent -> retrieve/refresh the credit report.
   */
  onRetrieveApprovalOdds: () => void;
  oddsConsentModal: React.ReactNode;
};

/**
 * Encapsulates the approval-odds retrieval flow shared by the comparison table
 * and the personal-loan card listing. Drives both the unlock (first retrieval)
 * and refresh entry points:
 *   1. no identity        -> redirect to login
 *   2. no consent yet     -> open the consent modal
 *   3. consent in place   -> retrieve (or refresh) the credit report
 *
 * Renders the consent modal itself and returns it as `oddsConsentModal` so
 * callers only need to drop it into their tree.
 */
export function useApprovalOddsRetrieval({
  approvalOddsData,
  sendConsent,
  langPrefix,
  chcBaseUrl,
}: UseApprovalOddsRetrievalParams): UseApprovalOddsRetrievalReturn {
  const identity = useIdentity();
  const oddsModalRef = useRef<SlideUpModalHandle>(null);

  const { approvalOddsAgreement, hasApprovalOddsConsent } = useMemo<{
    approvalOddsAgreement: AgreementsData | undefined;
    hasApprovalOddsConsent: boolean;
  }>(() => {
    const langCode = languageCode(langPrefix);

    const agreement = approvalOddsData?.agreements.find(
      (a) => a.locale && languageCode(a.locale) === langCode,
    );

    return {
      approvalOddsAgreement: agreement
        ? {
            ...agreement,
            documentId: String(agreement?.documentId),
            name: String(agreement?.name),
            title: String(agreement?.title),
            version: Number(agreement?.version),
            content: String(agreement?.content),
            locale: String(agreement?.locale),
            createdBy: '',
            updatedBy: '',
            __v: 0,
            _id: '',
          }
        : undefined,
      hasApprovalOddsConsent: Boolean(approvalOddsData?.consents.length),
    };
  }, [approvalOddsData, langPrefix]);

  const retrieveCreditReport = useCallback(() => {
    if (!chcBaseUrl) {
      throw new Error('Missing CHC base URL in CMS config');
    }

    const chcLangCode = languageCode(langPrefix);

    window.location.href = new URL(
      `${chcLangCode}/auth?redirectAfterCreditReportRetrieval=${encodeURIComponent(window.location.href)}`,
      chcBaseUrl,
    ).href;
  }, [langPrefix, chcBaseUrl]);

  const onRetrieveApprovalOdds = useCallback(() => {
    if (!identity) {
      window.location.href = `${langPrefix}/login?redirect_uri=${encodeURIComponent(window.location.href)}`;
      return;
    }

    if (!hasApprovalOddsConsent) {
      if (!approvalOddsAgreement) {
        throw new Error('Missing approval odds agreement');
      }

      oddsModalRef.current?.open();
      return;
    }

    retrieveCreditReport();
  }, [
    approvalOddsAgreement,
    hasApprovalOddsConsent,
    identity,
    langPrefix,
    retrieveCreditReport,
  ]);

  const handleApprovalOddsConsent = useCallback(async () => {
    if (!approvalOddsAgreement) {
      throw new Error('Missing approval odds agreement');
    }

    if (!hasApprovalOddsConsent) {
      try {
        await sendConsent([
          {
            agreementId: approvalOddsAgreement.documentId,
            agreementVersion: approvalOddsAgreement.version,
          },
        ]);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        console.error('Failed to send approval odds consent', error);
        window.alert(`Failed to update approval odds consent: ${message}`);
        return;
      }
    }

    oddsModalRef.current?.close();

    if (
      !approvalOddsData?.lastUpdated ||
      isApprovalOddsTooStale(approvalOddsData.lastUpdated)
    ) {
      // If there's no last updated date or the approval odds are too stale, we want to retrieve the credit report to get fresh data
      retrieveCreditReport();
    }
  }, [
    approvalOddsAgreement,
    approvalOddsData?.lastUpdated,
    hasApprovalOddsConsent,
    retrieveCreditReport,
    sendConsent,
  ]);

  const oddsConsentModal = (
    <OddsConsentModal
      slideUpModalRef={oddsModalRef}
      agreement={approvalOddsAgreement}
      onClickAgree={handleApprovalOddsConsent}
      pushDataLayer={pushDataLayer}
    />
  );

  return {
    onRetrieveApprovalOdds,
    oddsConsentModal,
  };
}
